import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface AIJobStatus {
  id: string;
  type: string;
  status: 'waiting' | 'active' | 'completed' | 'failed' | 'delayed';
  progress: number;
  result: any | null;
  error: string | null;
  createdAt: number;
  processedAt: number | null;
  finishedAt: number | null;
}

interface JobStatusResponse {
  success: boolean;
  data?: AIJobStatus;
  message?: string;
}

interface JobResultResponse {
  success: boolean;
  completed: boolean;
  data?: any;
  error?: string;
  status?: string;
  progress?: number;
}

/**
 * Get job status
 */
export const getJobStatus = async (jobId: string): Promise<JobStatusResponse> => {
  const response = await api.get(`/ai-jobs/${jobId}/status`);
  return response.data;
};

/**
 * Get job result
 */
export const getJobResult = async (jobId: string): Promise<JobResultResponse> => {
  const response = await api.get(`/ai-jobs/${jobId}/result`);
  return response.data;
};

/**
 * Poll for job completion
 * @param jobId - Job ID to poll
 * @param onProgress - Callback for progress updates
 * @param intervalMs - Polling interval in ms (default 2000)
 * @param timeoutMs - Max time to poll in ms (default 600000 = 10 minutes).
 *   Gerar uma landing page inteira leva 2-3 min; o antigo teto de 2 min
 *   derrubava o pedido no meio e o usuário via "IA indisponível".
 */
export const pollJobUntilComplete = (
  jobId: string,
  onProgress?: (progress: number, status: string) => void,
  intervalMs = 2000,
  timeoutMs = 600000
): Promise<any> => {
  return new Promise((resolve, reject) => {
    const startTime = Date.now();
    let pollInterval: NodeJS.Timeout | undefined;
    let settled = false;
    let consecutiveErrors = 0;
    const MAX_CONSECUTIVE_ERRORS = 5;

    // O primeiro poll roda ANTES do setInterval existir. Sem esta flag, um job
    // que já terminou resolvia com clearInterval(undefined) e o setInterval
    // criado logo depois ficava pollando pra sempre em segundo plano.
    const stop = () => {
      settled = true;
      if (pollInterval) clearInterval(pollInterval);
    };

    const poll = async () => {
      if (settled) return;
      try {
        // Check timeout
        if (Date.now() - startTime > timeoutMs) {
          stop();
          const secs = Math.round(timeoutMs / 1000);
          reject(new Error(
            `A geração passou de ${secs}s e o navegador parou de esperar. ` +
            'Ela continua rodando no servidor — tente de novo em instantes ou peça algo mais enxuto.'
          ));
          return;
        }

        const result = await getJobResult(jobId);
        consecutiveErrors = 0; // Reset on success

        if (result.completed) {
          stop();
          if (result.success) {
            resolve(result.data);
          } else {
            reject(new Error(result.error || 'Job failed'));
          }
          return;
        }

        // Report progress
        if (onProgress && result.progress !== undefined) {
          onProgress(result.progress, result.status || 'processing');
        }
      } catch (error: any) {
        // If it's a 404, the job might have been cleaned up
        if (error.response?.status === 404) {
          stop();
          reject(new Error('Job not found - it may have expired'));
          return;
        }
        // If it's a 422, the job failed (backend returns 422 for failed jobs)
        if (error.response?.status === 422) {
          stop();
          const failedData = error.response?.data;
          reject(new Error(failedData?.error || failedData?.message || 'Job failed'));
          return;
        }
        // For other errors, track consecutive failures
        consecutiveErrors++;
        console.warn(`Polling error (${consecutiveErrors}/${MAX_CONSECUTIVE_ERRORS}):`, error.message);
        if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
          stop();
          reject(new Error('Server connection lost. Please try again.'));
          return;
        }
      }
    };

    // Start polling
    poll();
    pollInterval = setInterval(poll, intervalMs);
  });
};

/**
 * Submit an async AI request and poll for result
 * This is a helper that combines the submit and poll steps
 */
export const submitAndPoll = async <T>(
  submitFn: () => Promise<{ success: boolean; async?: boolean; jobId?: string; data?: T }>,
  onProgress?: (progress: number, status: string) => void,
  intervalMs = 2000,
  timeoutMs = 600000
): Promise<T> => {
  const response = await submitFn();

  // If not async, return data directly
  if (!response.async || !response.jobId) {
    return response.data as T;
  }

  // Poll for result
  const result = await pollJobUntilComplete(
    response.jobId,
    onProgress,
    intervalMs,
    timeoutMs
  );

  return result.data || result;
};

export default {
  getJobStatus,
  getJobResult,
  pollJobUntilComplete,
  submitAndPoll
};
